import type { UpdateUserProfileDto, User } from '@/types/user.types';
import {
  assignPrimaryContactFields,
  hydratePhoneFromApi,
  resolveStoredContact,
} from '@/utils/user-contact-utils';

/** Write the typed primary contact onto email or phone and set `primaryContactKind`. */
export function applyProfilePrimaryContactChange(
  form: UpdateUserProfileDto,
  value: string,
  type: 'email' | 'phone',
): UpdateUserProfileDto {
  return {
    ...form,
    email: type === 'email' ? value : form.email,
    phone: type === 'phone' ? value : form.phone,
    primaryContactKind: type,
  };
}

/** Build `PATCH` profile DTO: primary channel + the other as secondary. */
export function buildUpdateProfileDto(params: {
  firstName?: string;
  lastName?: string;
  primaryContact: string;
  primaryContactType: 'email' | 'phone';
  primaryContactCountryCode: string;
  secondaryEmail?: string;
  secondaryPhone?: string;
  secondaryPhoneCountryCode: string;
}): UpdateUserProfileDto {
  const dto: UpdateUserProfileDto = {
    firstName: params.firstName,
    lastName: params.lastName,
  };
  assignPrimaryContactFields({
    dto,
    primaryValue: params.primaryContact,
    primaryKind: params.primaryContactType,
    primaryCountry: params.primaryContactCountryCode,
    secondaryValue:
      params.primaryContactType === 'email' ? params.secondaryPhone : params.secondaryEmail,
    secondaryCountry: params.secondaryPhoneCountryCode,
    defaultCountry: params.primaryContactCountryCode,
  });
  return dto;
}

/** Prefill edit-profile fields from GET user + stored primary contact. */
export function hydrateEditFormFromUser(
  profile: Pick<User, 'firstName' | 'lastName' | 'phone' | 'email' | 'primaryContactKind'>,
  defaultCountry: string,
): {
  initialData: UpdateUserProfileDto;
  phoneCountryCode: string;
  primaryContact: string;
  primaryContactType: 'email' | 'phone';
  primaryContactCountryCode: string;
} {
  const hydratedPhone = hydratePhoneFromApi(profile.phone, defaultCountry);
  const stored = resolveStoredContact(
    profile.email ?? null,
    profile.phone ?? null,
    profile.primaryContactKind,
  );
  return {
    initialData: {
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      phone: hydratedPhone.value || undefined,
    },
    phoneCountryCode: hydratedPhone.countryCode,
    primaryContact: stored.displayValue,
    primaryContactType: stored.mode,
    primaryContactCountryCode: stored.isoCode,
  };
}
